import { Box, FormControlLabel, FormGroup, Radio, RadioGroup } from "@mui/material";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import Link from "@mui/material/Link";
import { useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router";
import { CheckBox } from "../../../../../../component/basic/inputs/checkbox/check_box";
import { XStack, YStack } from "../../../../../../component/basic/surfaces/box/XStack";
import {
    CreateExamKind,
    CreateExamOption,
    MathCreateKind,
} from "../../../../../../model/exam/Exam";
import { RoleKind } from "../../../../../../model/Role";
import { actionCreateSimulate } from "../../../../../../store/exam/action";
import { actionSetCreateDialog } from "../../../../../../store/exam/reducer";
import { selectExamCreateDialog } from "../../../../../../store/exam/selector";
import { useAppDispatch } from "../../../../../../store/hooks";
import { selectUser } from "../../../../../../store/user/selector";
import { Colors } from "../../../../../../tools/color";
import { LogDebug } from "../../../../../../tools/log";
import { OpenURL } from "../../../../../../tools/url";

interface CreateExamDialogProps {}

export function CreateExamDialog(props: CreateExamDialogProps) {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const location = useLocation();

    const enable = useSelector(selectExamCreateDialog);

    const [opt, setOpt] = useState({
        version: 2,
        kind: CreateExamKind.FullExam,
        include_done: false,
        with_issue: true,
        math_create_kind: MathCreateKind.Real,
    } as CreateExamOption);

    const kinds = [
        { name: "全真模考（5个Section）", val: CreateExamKind.FullExam },
        { name: "只做Verbal（2个Section）", val: CreateExamKind.OnlyVerbal },
        {
            name: "只做Quantitative（2个Section）",
            val: CreateExamKind.OnlyQuantitative,
        },
        { name: "只做作文（1个Section）", val: CreateExamKind.OnlyIssue },
    ];

    const mathKinds = [
        { name: "普通模式", val: MathCreateKind.Real },
        { name: "挑战模式", val: MathCreateKind.Challenge },
    ];

    const radioGroupRef = useRef<HTMLElement>(null);

    const checkValid = (): boolean => {
        if (opt.version === -1) {
            return false;
        }
        if (opt.kind === -1) {
            return false;
        }
        return true;
    };

    const onClose = () => {
        dispatch(actionSetCreateDialog(false));
    };

    const onClick = () => {
        if (checkValid()) {
            LogDebug("create exam", opt);
            dispatch(actionCreateSimulate(opt, navigate, location.pathname + location.search));
            dispatch(actionSetCreateDialog(false));
        }
    };

    const user = useSelector(selectUser);
    const canUseMathKind =
        user.authority.role_kind === RoleKind.VIP || user.authority.role_kind === RoleKind.SVIP;

    return (
        <Dialog open={enable} onClose={onClose} fullWidth maxWidth="sm">
            <DialogContent>
                <XStack sx={{ alignItems: "start" }}>
                    <Box>
                        <h4>模考类型</h4>
                        <RadioGroup
                            ref={radioGroupRef}
                            value={opt.kind}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                const i = parseInt(event.target.value);
                                if (i === undefined) {
                                    return;
                                }
                                let opt2 = { ...opt, kind: i };
                                if (opt2.kind === CreateExamKind.OnlyIssue) {
                                    opt2.with_issue = true;
                                } else if (opt2.kind === CreateExamKind.OnlyVerbal) {
                                    opt2.with_issue = false;
                                } else if (opt2.kind === CreateExamKind.OnlyQuantitative) {
                                    opt2.with_issue = false;
                                } else if (opt2.kind === CreateExamKind.FullExam) {
                                    opt2.with_issue = true;
                                }
                                setOpt(opt2);
                            }}
                        >
                            {kinds.map((v, index) => (
                                <FormControlLabel
                                    value={v.val}
                                    key={index}
                                    control={<Radio />}
                                    label={v.name}
                                />
                            ))}
                        </RadioGroup>
                    </Box>

                    <Box sx={{ ml: "20px" }}>
                        <h4>&nbsp;</h4>
                        <RadioGroup
                            value={opt.math_create_kind}
                            onChange={(e) => {
                                const x = parseInt(e.target.value);
                                if (x === undefined) {
                                    return;
                                }
                                setOpt({ ...opt, math_create_kind: x });
                            }}
                        >
                            {mathKinds.map((v, index) => (
                                <FormControlLabel
                                    value={v.val}
                                    key={index}
                                    control={<Radio />}
                                    label={v.name}
                                    disabled={
                                        canUseMathKind &&
                                        (opt.kind === CreateExamKind.OnlyIssue ||
                                            opt.kind === CreateExamKind.OnlyVerbal)
                                    }
                                />
                            ))}
                        </RadioGroup>
                        <Box sx={{ ml: "30px", mt: "-5px" }}>
                            <Link
                                color="primary"
                                onClick={() => {
                                    OpenURL("https://mp.weixin.qq.com/s/41tu-H9uYLldHwItshsjtA");
                                }}
                            >
                                什么时候需要选挑战模式？→
                            </Link>
                        </Box>
                    </Box>
                </XStack>

                <hr />

                <FormGroup>
                    <CheckBox
                        checked={!opt.include_done}
                        onClick={(b) => setOpt({ ...opt, include_done: !b })}
                        label="优先抓未做过的题"
                    />
                    <CheckBox
                        checked={opt.with_issue}
                        onClick={(b) => {
                            const opt2 = { ...opt, with_issue: b };
                            setOpt(opt2);
                        }}
                        label="包括作文"
                        disabled={
                            opt.kind === CreateExamKind.OnlyQuantitative ||
                            opt.kind === CreateExamKind.OnlyVerbal ||
                            opt.kind === CreateExamKind.OnlyIssue
                        }
                    />
                    <CheckBox
                        checked={opt.enable_count_up}
                        onClick={(b) => {
                            const opt2 = { ...opt, enable_count_up: b } as CreateExamOption;
                            setOpt(opt2);
                        }}
                        label="模考不限时"
                    />
                    <YStack align="left">
                        <CheckBox
                            checked={opt.pick_first}
                            onClick={(b) => {
                                const opt2 = { ...opt, pick_first: b } as CreateExamOption;
                                setOpt(opt2);
                            }}
                            disabled={
                                !(
                                    user.authority.role_kind === RoleKind.VIP ||
                                    user.authority.role_kind === RoleKind.SVIP
                                )
                            }
                            label={<>优先抓精选题库（BB大会员/BB超级大会员 专属）</>}
                        />
                        <YStack align="left" sx={{ ml: "30px", mt: "-5px" }}>
                            <Link
                                color="primary"
                                onClick={() => {
                                    OpenURL("https://mp.weixin.qq.com/s/67GA63UmKHA3Zek6_rNR0g");
                                }}
                            >
                                如何在考场开卷考试（上）→
                            </Link>
                            <Link
                                color="primary"
                                onClick={() => {
                                    OpenURL("https://mp.weixin.qq.com/s/GFcrZ367E4C_YwqMiXliqw");
                                }}
                            >
                                如何在考场开卷考试（下）→
                            </Link>
                        </YStack>
                    </YStack>
                </FormGroup>
            </DialogContent>
            <DialogActions>
                <XStack sx={{ justifyContent: "end", fontSize: 12, color: Colors["bbgray"] }}>
                    创建模考过程需要10-60s，请耐心等待
                </XStack>
                <Button onClick={onClick} autoFocus>
                    <b>创建</b>
                </Button>
                <Button onClick={onClose}>
                    <b>取消</b>
                </Button>
            </DialogActions>
        </Dialog>
    );
}
